import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowLeft, Download, Award, BookOpen, DollarSign, TrendingUp, User, CheckCircle2, XCircle, Loader2, IdCard, PlayCircle, Clock, FileText, Receipt } from 'lucide-react';

const statusColor = (s) => ({
  payment_pending: 'bg-amber-100 text-amber-700',
  active: 'bg-brand-success/15 text-brand-success',
  completed: 'bg-brand-primary/15 text-brand-primary',
  cancelled: 'bg-slate-100 text-slate-500',
}[s] || 'bg-slate-100 text-slate-500');

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState('');

  useEffect(() => {
    api.get(`/students/${id}/full-view`).then(r => setData(r.data)).catch(err => {
      toast.error(formatError(err.response?.data?.detail));
    }).finally(() => setLoading(false));
  }, [id]);

  const downloadIdCard = async () => {
    setDownloadingId('idcard');
    try {
      const res = await api.get(`/students/${id}/id-card`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `idcard-${data?.student?.student_id || id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('ID card downloaded');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDownloadingId(''); }
  };

  const downloadCert = async (cert) => {
    setDownloadingId(cert.id);
    try {
      const res = await api.get(`/certificates/${cert.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `certificate-${cert.certificate_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch { toast.error('Failed'); }
    finally { setDownloadingId(''); }
  };

  const downloadInvoiceForEnrollment = async (enrollmentId) => {
    setDownloadingId(`inv-${enrollmentId}`);
    try {
      let inv;
      try {
        inv = (await api.get(`/academy/invoices/by-enrollment/${enrollmentId}`)).data;
      } catch (e) {
        if (e.response?.status === 404) {
          // Backfill for enrollments approved before invoicing was live
          inv = (await api.post(`/academy/invoices/generate/${enrollmentId}`)).data;
        } else {
          throw e;
        }
      }
      const res = await api.get(`/academy/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `invoice-${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to download invoice');
    } finally { setDownloadingId(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-brand-primary" size={32} /></div>;
  if (!data) return <div className="p-6">Student not found</div>;

  const { student, enrollments, exam_attempts, certificates, payment_summary, stats } = data;

  return (
    <div className="space-y-5" data-testid="student-detail-page">
      <button onClick={() => navigate('/academy')} className="flex items-center gap-1 text-sm text-brand-text-secondary hover:text-brand-primary" data-testid="back-to-academy">
        <ArrowLeft size={14} /> Back to Academy
      </button>

      {/* Profile Hero */}
      <Card className="border-brand-border bg-gradient-to-br from-[#0F1E42] to-[#2563EB] text-white overflow-hidden">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-amber-400 flex items-center justify-center">
                <span className="font-heading text-2xl font-bold text-slate-900">{(student.name || 'S').split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()}</span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold">Quantora Academy Student</p>
                <h1 className="font-heading text-2xl font-bold" data-testid="student-name">{student.name}</h1>
                <p className="text-sm text-white/80 font-mono">{student.student_id} · {student.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={downloadIdCard} disabled={downloadingId === 'idcard'} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="download-id-card">
                {downloadingId === 'idcard' ? <Loader2 size={14} className="animate-spin" /> : <><IdCard size={14} className="mr-1.5" /> Download ID Card</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { l: 'Enrolled', v: stats.total_courses_enrolled, i: BookOpen, c: 'text-brand-primary' },
          { l: 'Active', v: payment_summary.active_count, i: PlayCircle, c: 'text-brand-success' },
          { l: 'Completed', v: payment_summary.completed_count, i: CheckCircle2, c: 'text-brand-secondary' },
          { l: 'Certificates', v: stats.certificates_earned, i: Award, c: 'text-amber-500' },
          { l: 'Paid', v: `$${payment_summary.total_paid}`, i: DollarSign, c: 'text-brand-success' },
          { l: 'Pending', v: `$${payment_summary.pending}`, i: Clock, c: 'text-amber-600' },
        ].map(s => (
          <Card key={s.l} className="border-brand-border bg-white">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-brand-text-secondary font-semibold">{s.l}</p>
                  <p className="font-heading text-lg font-bold text-brand-text-primary">{s.v}</p>
                </div>
                <s.i size={16} className={s.c} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="courses">
        <TabsList className="bg-white border border-brand-border rounded-sm p-1 h-auto">
          <TabsTrigger value="courses" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-courses">Courses ({enrollments.length})</TabsTrigger>
          <TabsTrigger value="exams" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-exams">Exam Attempts ({exam_attempts.length})</TabsTrigger>
          <TabsTrigger value="certificates" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-certificates">Certificates ({certificates.length})</TabsTrigger>
          <TabsTrigger value="profile" className="text-xs data-[state=active]:bg-brand-primary data-[state=active]:text-white" data-testid="tab-profile">Profile</TabsTrigger>
        </TabsList>

        {/* COURSES + PROGRESS */}
        <TabsContent value="courses" className="mt-4 space-y-3">
          {enrollments.length === 0 ? (
            <Card className="border-brand-border bg-white"><CardContent className="p-10 text-center text-brand-text-secondary">No enrollments yet</CardContent></Card>
          ) : enrollments.map(e => {
            const cd = e.course_details || {};
            const total = cd.total_lessons || 0;
            const completed = (e.lesson_progress || []).filter(p => p.completed).length;
            const pct = total ? Math.round((completed / total) * 100) : (e.progress_pct || 0);
            return (
              <Card key={e.id} className="border-brand-border bg-white" data-testid={`enrollment-${e.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div>
                      <h3 className="font-heading text-base font-bold text-brand-text-primary">{e.course_title}</h3>
                      <p className="text-[11px] text-brand-text-secondary">{cd.level} · {cd.category} · {cd.duration_weeks} weeks · Pass ≥ {cd.pass_percentage}%</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`text-[10px] border-0 ${statusColor(e.status)}`}>{e.status?.replace('_', ' ')}</Badge>
                      <span className="text-xs font-mono text-brand-primary">${e.fee_amount}</span>
                      {(e.status === 'active' || e.status === 'completed') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadInvoiceForEnrollment(e.id)}
                          disabled={downloadingId === `inv-${e.id}`}
                          className="text-xs h-7 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white"
                          data-testid={`download-invoice-${e.id}`}
                        >
                          {downloadingId === `inv-${e.id}` ? <Loader2 size={12} className="animate-spin" /> : <><Receipt size={12} className="mr-1" /> Invoice</>}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] text-brand-text-secondary mb-1 flex justify-between">
                    <span>Payment: {e.payment_mode?.toUpperCase()} {e.payment_reference ? `· ${e.payment_reference}` : ''}</span>
                    <span>Enrolled {(e.enrolled_at || '').slice(0, 10)}</span>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-[11px] mb-0.5">
                      <span className="font-semibold">Progress</span>
                      <span className="font-mono">{completed}/{total} lessons · {pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-brand-bg overflow-hidden">
                      <div className="h-full bg-brand-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  {(e.lesson_progress || []).length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs text-brand-primary cursor-pointer hover:underline">Lesson-by-lesson progress</summary>
                      <table className="w-full mt-2 text-[11px]">
                        <thead><tr className="border-b border-brand-border/50">
                          {['Lesson #', 'Watched %', 'Completed', 'Last Position', 'Updated'].map(h => <th key={h} className="text-left py-1 text-brand-text-secondary font-semibold">{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {e.lesson_progress.sort((a, b) => a.lesson_index - b.lesson_index).map(p => (
                            <tr key={p.lesson_index} className="border-b border-brand-border/30">
                              <td className="py-1">L{p.lesson_index + 1}</td>
                              <td className="py-1 font-mono">{Math.round(p.watched_pct || 0)}%</td>
                              <td className="py-1">{p.completed ? <CheckCircle2 size={12} className="text-brand-success" /> : <span className="text-brand-text-secondary/60">—</span>}</td>
                              <td className="py-1 font-mono text-brand-text-secondary">{Math.floor((p.last_position_sec || 0) / 60)}:{String(Math.round((p.last_position_sec || 0) % 60)).padStart(2, '0')}</td>
                              <td className="py-1 text-brand-text-secondary">{(p.updated_at || '').slice(0, 10)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* EXAM Q&A */}
        <TabsContent value="exams" className="mt-4 space-y-3">
          {exam_attempts.length === 0 ? (
            <Card className="border-brand-border bg-white"><CardContent className="p-10 text-center text-brand-text-secondary">No exams attempted yet</CardContent></Card>
          ) : exam_attempts.map(a => (
            <Card key={a.id} className="border-brand-border bg-white" data-testid={`attempt-${a.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-sm">{a.course_details?.title || a.course_title}</CardTitle>
                    <p className="text-[11px] text-brand-text-secondary">Attempted {(a.attempted_at || '').slice(0, 10)} · {a.correct}/{a.total} correct</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] border-0 ${a.passed ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>{a.passed ? 'PASSED' : 'FAILED'}</Badge>
                    <span className={`font-heading text-2xl font-bold ${a.passed ? 'text-brand-success' : 'text-brand-error'}`}>{a.score_pct}%</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="space-y-2" data-testid={`qa-list-${a.id}`}>
                  {(a.question_breakdown || []).map(q => (
                    <div key={q.q_num} className={`border rounded-lg p-3 ${q.is_correct ? 'border-brand-success/30 bg-brand-success/5' : 'border-brand-error/30 bg-brand-error/5'}`}>
                      <div className="flex items-start gap-2">
                        {q.is_correct ? <CheckCircle2 size={16} className="text-brand-success shrink-0 mt-0.5" /> : <XCircle size={16} className="text-brand-error shrink-0 mt-0.5" />}
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-brand-text-primary">Q{q.q_num}. {q.question}</p>
                          <div className="mt-1.5 text-[12px] space-y-0.5">
                            <p><span className="text-brand-text-secondary">Student answered:</span> <span className={q.is_correct ? 'text-brand-success font-semibold' : 'text-brand-error font-semibold'}>{q.student_answer_text}</span></p>
                            {!q.is_correct && (
                              <p><span className="text-brand-text-secondary">Correct answer:</span> <span className="text-brand-success font-semibold">{q.correct_answer_text}</span></p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* CERTIFICATES */}
        <TabsContent value="certificates" className="mt-4 space-y-3">
          {certificates.length === 0 ? (
            <Card className="border-brand-border bg-white"><CardContent className="p-10 text-center text-brand-text-secondary">No certificates earned yet</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {certificates.map(c => (
                <Card key={c.id} className="border-brand-border bg-white" data-testid={`cert-${c.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0">
                        <Award size={20} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-sm font-bold text-brand-text-primary">{c.course_title}</h3>
                        <p className="text-[11px] text-brand-text-secondary font-mono">{c.certificate_number}</p>
                        <p className="text-[11px] text-brand-text-secondary">Score {Math.round(c.score_pct)}% · {(c.issued_at || '').slice(0, 10)}</p>
                      </div>
                      <Button size="sm" onClick={() => downloadCert(c)} disabled={downloadingId === c.id} className="bg-brand-primary text-white text-xs" data-testid={`download-cert-${c.id}`}>
                        {downloadingId === c.id ? <Loader2 size={12} className="animate-spin" /> : <><Download size={12} className="mr-1" /> PDF</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PROFILE */}
        <TabsContent value="profile" className="mt-4">
          <Card className="border-brand-border bg-white max-w-lg">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><User size={14} /> Profile</CardTitle></CardHeader>
            <CardContent>
              {[
                ['Student ID', student.student_id],
                ['Full Name', student.name],
                ['Email', student.email],
                ['Phone', student.phone || '-'],
                ['Status', student.status?.toUpperCase()],
                ['Joined', (student.created_at || '').slice(0, 10)],
                ['Total Enrollments', stats.total_courses_enrolled],
                ['Total Paid', `$${payment_summary.total_paid}`],
                ['Pending Payments', `$${payment_summary.pending}`],
                ['Certificates', stats.certificates_earned],
                ['Exam Attempts', stats.exam_attempts],
                ['Exams Passed', stats.exam_pass_count],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between py-2 border-b border-brand-border/50">
                  <span className="text-xs text-brand-text-secondary">{l}</span>
                  <span className="text-xs font-medium text-brand-text-primary">{v || '-'}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
